import { lightenHexColor, normalizeHexColor } from './richText.js';
import { normalizeStoredImageItem, revokeLocalImageUrl } from './localImage.js';
import { getQiniuSignedUrl } from './qiniuMedia.js';
import { v4 as uuidv4 } from 'uuid';
import { LOCAL_ASSET } from './localAssets.js';

export const QRCODE_NAV_TYPES = {
	solid: 'qrcode-nav-solid',
	gradient: 'qrcode-nav-gradient',
	outline: 'qrcode-nav-outline',
	cell: 'qrcode-nav-cell',
	card: 'qrcode-nav-card',
	list: 'qrcode-nav-list',
	banner: 'qrcode-nav-banner',
	link: 'qrcode-nav-link',
	grid: 'qrcode-nav-grid',
};

const DEFAULT_NAV_IMAGE = {
	id: 'qrcode-nav-default-image',
	filename: 'demo.png',
	previewUrl: LOCAL_ASSET.qrcodeNavCell,
	url: '',
	local: false,
};

const DEFAULT_CARD_IMAGE = {
	id: 'qrcode-nav-card-default',
	filename: 'qrcode-nav-default.png',
	previewUrl: LOCAL_ASSET.qrcodeNavCard,
	url: '',
	local: false,
};

const DEFAULT_LIST_ITEM_PRESETS = [
	{ text: '灯塔：那是光照进来的地方',  },
	{ text: '楼梯：闪闪发光的螺旋楼梯',  },
	{ text: '露台： 阳光在这里凝固',  },
];

const DEFAULT_LIST_ITEM_TEXT_COLOR = '#333333';

const DEFAULT_CARD_DESCRIPTION = 'LOUXOR卢索系列由托马斯.巴斯蒂德 (THOMAS BASTIDE) 创作，从埃及著名考古遗址取得灵感，也受到乔治.谢瓦利埃首批巴卡拉珠宝系作品启发';

const DEFAULT_BANNER_DESCRIPTION = '一物一码，一台手机就能查看最新档案；设备档案管理、报修、巡检、保养，全流程覆盖';

const DEFAULT_LINK_ITEM_PRESETS = [
	{
		title: 'OMTANKSAM 沃姆安克萨姆',
		subtitle: '哑光表面不会刺激双眼，柔滑边缘提供舒适触感',
	},
	{
		title: 'AGAM 阿甘实木高脚椅',
		subtitle: '高脚椅太高？普通椅子又太矮？儿童椅将是你的理想选择',
	},
	{
		title: 'TJUSIG 图希格衣帽收纳系列',
		subtitle: '设计风格清新简约，搭配可持续的木材质材料',
	},
];

const DEFAULT_LINK_ITEM_TITLE_COLOR = '#333333';
const DEFAULT_LINK_ITEM_SUBTITLE_COLOR = '#999999';

// 跳转链接快捷网格：4 列 × N 行，每个格子是彩色圆角图标 + 下方一行文字
const DEFAULT_GRID_COLORS = ['#ff7d40', '#5fce66', '#ff4b4b', '#3d73c7', '#a067e6', '#f5b73b', '#1a9e96', '#ec407a'];
const DEFAULT_GRID_ITEM_PRESETS = [
	{ text: '公告' },
	{ text: '财报' },
	{ text: '审批' },
	{ text: '周报' },
	{ text: '管理' },
	{ text: '计划' },
	{ text: '会议' },
	{ text: '打卡' },
];
const DEFAULT_GRID_ITEM_TEXT_COLOR = '#333333';

const DEFAULTS_BY_TYPE = {
	[QRCODE_NAV_TYPES.solid]: {
		buttonText: '查看详情',
		jumpLink: null,
		buttonWidth: 0,
		borderRadius: 4,
		bgColor: '#3d73c7',
		textColor: '#ffffff',
	},
	[QRCODE_NAV_TYPES.gradient]: {
		buttonText: '查看详情',
		jumpLink: null,
		buttonWidth: 0,
		borderRadius: 22,
		bgColor: '#3d73c7',
		textColor: '#ffffff',
	},
	[QRCODE_NAV_TYPES.outline]: {
		buttonText: '查看详情',
		jumpLink: null,
		buttonWidth: 0,
		borderRadius: 22,
		textColor: '#3d73c7',
	},
	[QRCODE_NAV_TYPES.cell]: {
		title: '米家电热水壶使用说明',
		actionText: '点击查看',
		showImage: true,
		image: { ...DEFAULT_NAV_IMAGE },
		jumpLink: null,
		borderRadius: 8,
		bgColor: '#f2f2f2',
		textColor: '#333333',
	},
	[QRCODE_NAV_TYPES.card]: {
		title: 'LOUXOR卢索系列',
		description: DEFAULT_CARD_DESCRIPTION,
		actionText: '了解更多',
		showImage: true,
		image: { ...DEFAULT_CARD_IMAGE },
		jumpLink: null,
		borderRadius: 8,
		titleColor: '#333333',
		detailColor: '#999999',
	},
	[QRCODE_NAV_TYPES.list]: {
		items: [],
	},
	[QRCODE_NAV_TYPES.banner]: {
		title: '智能巡检解决方案',
		description: DEFAULT_BANNER_DESCRIPTION,
		jumpLink: null,
		borderRadius: 8,
		bgColor: '#1a9e96',
		textColor: '#ffffff',
	},
	[QRCODE_NAV_TYPES.link]: {
		items: [],
	},
	[QRCODE_NAV_TYPES.grid]: {
		items: [],
	},
};

export function createDefaultListItem(preset = {}, index = 0) {
	const text = preset.text
		|| (preset.title && preset.description ? `${preset.title}�?{preset.description}` : '')
		|| '标题：描述文';
	return {
		id: uuidv4(),
		text,
		textColor: DEFAULT_LIST_ITEM_TEXT_COLOR,
		image: {
			id: `qrcode-nav-list-item-${uuidv4()}`,
			filename: 'demo.jpg',
			previewUrl: LOCAL_ASSET.qrcodeNavItems[index % LOCAL_ASSET.qrcodeNavItems.length],
			url: '',
			local: false,
		},
		jumpLink: null,
	};
}

export function createDefaultListItems(count = 3) {
	const items = [];
	for (let i = 0; i < count; i += 1) {
		const preset = DEFAULT_LIST_ITEM_PRESETS[i] || {};
		items.push(createDefaultListItem(preset, i));
	}
	return items;
}

export function createDefaultLinkItem(preset = {}, index = 0) {
	const presetData = DEFAULT_LINK_ITEM_PRESETS[index] || {};
	return {
		id: uuidv4(),
		title: preset.title ?? presetData.title ?? '标题文字',
		subtitle: preset.subtitle ?? presetData.subtitle ?? '副标题描述文字',
		titleColor: DEFAULT_LINK_ITEM_TITLE_COLOR,
		subtitleColor: DEFAULT_LINK_ITEM_SUBTITLE_COLOR,
		image: {
			id: `qrcode-nav-link-item-${uuidv4()}`,
			filename: 'qrcode-nav-link.jpg',
			previewUrl: LOCAL_ASSET.qrcodeNavLink[index % LOCAL_ASSET.qrcodeNavLink.length],
			url: '',
			local: false,
		},
		jumpLink: null,
	};
}

export function createDefaultLinkItems(count = 3) {
	const items = [];
	for (let i = 0; i < count; i += 1) {
		items.push(createDefaultLinkItem({}, i));
	}
	return items;
}

export function createDefaultGridItem(preset = {}, index = 0) {
	const presetData = DEFAULT_GRID_ITEM_PRESETS[index] || {};
	return {
		id: uuidv4(),
		text: preset.text ?? presetData.text ?? '快捷入口',
		bgColor: preset.bgColor ?? DEFAULT_GRID_COLORS[index % DEFAULT_GRID_COLORS.length],
		textColor: preset.textColor ?? DEFAULT_GRID_ITEM_TEXT_COLOR,
		image: {
			id: `qrcode-nav-grid-item-${uuidv4()}`,
			filename: 'demo.png',
			previewUrl: LOCAL_ASSET.navGridItems[index % LOCAL_ASSET.navGridItems.length],
			url: '',
			local: false,
		},
		jumpLink: preset.jumpLink?.type ? { ...preset.jumpLink } : null,
	};
}

export function createDefaultGridItems(count = 8) {
	const items = [];
	for (let i = 0; i < count; i += 1) {
		items.push(createDefaultGridItem({}, i));
	}
	return items;
}

export function isQrcodeNavListBlock(blockType) {
	return blockType === QRCODE_NAV_TYPES.list;
}

export function isQrcodeNavCellBlock(blockType) {
	return blockType === QRCODE_NAV_TYPES.cell;
}

export function isQrcodeNavCardBlock(blockType) {
	return blockType === QRCODE_NAV_TYPES.card;
}

export function isQrcodeNavBannerBlock(blockType) {
	return blockType === QRCODE_NAV_TYPES.banner;
}

export function isQrcodeNavLinkBlock(blockType) {
	return blockType === QRCODE_NAV_TYPES.link;
}

export function isQrcodeNavGridBlock(blockType) {
	return blockType === QRCODE_NAV_TYPES.grid;
}

export function isQrcodeNavImageBlock(blockType) {
	return isQrcodeNavCellBlock(blockType) || isQrcodeNavCardBlock(blockType);
}

export function getQrcodeNavVariant(blockType) {
	if (blockType === QRCODE_NAV_TYPES.gradient) return 'gradient';
	if (blockType === QRCODE_NAV_TYPES.outline) return 'outline';
	if (blockType === QRCODE_NAV_TYPES.cell) return 'cell';
	if (blockType === QRCODE_NAV_TYPES.card) return 'card';
	if (blockType === QRCODE_NAV_TYPES.list) return 'list';
	if (blockType === QRCODE_NAV_TYPES.banner) return 'banner';
	if (blockType === QRCODE_NAV_TYPES.link) return 'link';
	if (blockType === QRCODE_NAV_TYPES.grid) return 'grid';
	return 'solid';
}

export function getQrcodeNavDefaults(blockType) {
	const defaults = DEFAULTS_BY_TYPE[blockType] || DEFAULTS_BY_TYPE[QRCODE_NAV_TYPES.solid];
	const result = { ...defaults };
	if (blockType === QRCODE_NAV_TYPES.cell && defaults.image) {
		result.image = { ...defaults.image };
	}
	if (blockType === QRCODE_NAV_TYPES.card && defaults.image) {
		result.image = { ...defaults.image };
	}
	if (blockType === QRCODE_NAV_TYPES.list) {
		result.items = createDefaultListItems(3);
	}
	if (blockType === QRCODE_NAV_TYPES.link) {
		result.items = createDefaultLinkItems(3);
	}
	if (blockType === QRCODE_NAV_TYPES.grid) {
		result.items = createDefaultGridItems(8);
	}
	return result;
}

export function normalizeQrcodeNavListItem(item = {}, index = 0) {
	const fallback = createDefaultListItem({}, index);
	let text = item.text;
	if (!text && item.title) {
		text = item.description ? `${item.title}�?{item.description}` : item.title;
	}
	return {
		id: item.id || uuidv4(),
		text: text || fallback.text,
		textColor: normalizeHexColor(item.textColor || item.titleColor || fallback.textColor),
		image: normalizeNavImage(item.image, fallback.image, 'qrcode-nav-list-image'),
		jumpLink: item.jumpLink?.type ? { ...item.jumpLink } : null,
	};
}

export function normalizeQrcodeNavListItems(items = []) {
	if (!items.length) {
		return createDefaultListItems(3);
	}
	return items.map((item, index) => normalizeQrcodeNavListItem(item, index));
}

export function normalizeQrcodeNavListData(data = {}) {
	return {
		items: normalizeQrcodeNavListItems(data.items),
	};
}

export function normalizeQrcodeNavLinkItem(item = {}, index = 0) {
	const fallback = createDefaultLinkItem({}, index);
	return {
		id: item.id || fallback.id,
		title: item.title?.trim?.() || fallback.title,
		subtitle: item.subtitle?.trim?.() || fallback.subtitle,
		titleColor: normalizeHexColor(item.titleColor || fallback.titleColor),
		subtitleColor: normalizeHexColor(item.subtitleColor || fallback.subtitleColor),
		image: normalizeNavImage(item.image, fallback.image, 'qrcode-nav-link-image'),
		jumpLink: item.jumpLink?.type ? { ...item.jumpLink } : null,
	};
}

export function normalizeQrcodeNavLinkItems(items = []) {
	if (!items.length) {
		return createDefaultLinkItems(3);
	}
	return items.map((item, index) => normalizeQrcodeNavLinkItem(item, index));
}

export function normalizeQrcodeNavLinkData(data = {}) {
	return {
		items: normalizeQrcodeNavLinkItems(data.items),
	};
}

export function normalizeQrcodeNavGridItem(item = {}, index = 0) {
	const fallback = createDefaultGridItem({}, index);
	return {
		id: item.id || fallback.id,
		text: item.text?.trim?.() || fallback.text,
		bgColor: normalizeHexColor(item.bgColor || fallback.bgColor),
		textColor: normalizeHexColor(item.textColor || fallback.textColor),
		image: normalizeNavImage(item.image, fallback.image, 'qrcode-nav-grid-image'),
		jumpLink: item.jumpLink?.type ? { ...item.jumpLink } : null,
	};
}

export function normalizeQrcodeNavGridItems(items = []) {
	if (!items.length) {
		return createDefaultGridItems(8);
	}
	return items.map((item, index) => normalizeQrcodeNavGridItem(item, index));
}

export function normalizeQrcodeNavGridData(data = {}) {
	return {
		items: normalizeQrcodeNavGridItems(data.items),
	};
}

export function getQrcodeNavImageUrl(image) {
	if (!image) return '';
	return getQiniuSignedUrl(image.previewUrl || image.url || '');
}

function normalizeNavImage(image, fallback, idPrefix = 'qrcode-nav-image') {
	if (!image?.previewUrl && !image?.url) {
		return fallback ? { ...fallback } : null;
	}
	return normalizeStoredImageItem(image, fallback || { id: idPrefix });
}

export function normalizeQrcodeNavData(blockType, data = {}) {
	if (blockType === QRCODE_NAV_TYPES.list) {
		return normalizeQrcodeNavListData(data);
	}

	const defaults = getQrcodeNavDefaults(blockType);
	const normalized = {
		...defaults,
		...data,
		jumpLink: data.jumpLink?.type ? { ...data.jumpLink } : null,
		borderRadius: Number.isFinite(Number(data.borderRadius)) ? Number(data.borderRadius) : defaults.borderRadius,
	};

	if (blockType === QRCODE_NAV_TYPES.cell) {
		normalized.title = data.title ?? defaults.title;
		normalized.actionText = data.actionText ?? defaults.actionText;
		normalized.showImage = data.showImage !== false;
		normalized.image = normalizeNavImage(data.image, defaults.image, 'qrcode-nav-cell-image');
		normalized.bgColor = normalizeHexColor(data.bgColor || defaults.bgColor);
		normalized.textColor = normalizeHexColor(data.textColor || defaults.textColor);
		return normalized;
	}

	if (blockType === QRCODE_NAV_TYPES.card) {
		normalized.title = data.title ?? defaults.title;
		normalized.description = data.description ?? defaults.description;
		normalized.actionText = data.actionText ?? defaults.actionText;
		normalized.showImage = data.showImage !== false;
		normalized.image = normalizeNavImage(data.image, defaults.image, 'qrcode-nav-card-image');
		normalized.titleColor = normalizeHexColor(data.titleColor || defaults.titleColor);
		normalized.detailColor = normalizeHexColor(data.detailColor || defaults.detailColor);
		return normalized;
	}

	if (blockType === QRCODE_NAV_TYPES.banner) {
		normalized.title = data.title ?? defaults.title;
		normalized.description = data.description ?? defaults.description;
		normalized.bgColor = normalizeHexColor(data.bgColor || defaults.bgColor);
		normalized.textColor = normalizeHexColor(data.textColor || defaults.textColor);
		return normalized;
	}

	if (blockType === QRCODE_NAV_TYPES.link) {
		return normalizeQrcodeNavLinkData(data);
	}

	if (blockType === QRCODE_NAV_TYPES.grid) {
		return normalizeQrcodeNavGridData(data);
	}

	normalized.buttonText = data.buttonText || defaults.buttonText;
	normalized.buttonWidth = Number.isFinite(Number(data.buttonWidth)) ? Number(data.buttonWidth) : defaults.buttonWidth;

	if (blockType !== QRCODE_NAV_TYPES.outline) {
		normalized.bgColor = normalizeHexColor(data.bgColor || defaults.bgColor);
	}
	normalized.textColor = normalizeHexColor(data.textColor || defaults.textColor);
	return normalized;
}

export function getQrcodeNavButtonStyle(blockType, data = {}) {
	const variant = getQrcodeNavVariant(blockType);
	const normalized = normalizeQrcodeNavData(blockType, data);
	const textColor = normalized.textColor;
	const bgColor = normalized.bgColor;
	const borderRadius = normalized.borderRadius;
	const width = normalized.buttonWidth > 0 ? `${normalized.buttonWidth}px` : '100%';

	const style = {
		width,
		borderRadius: `${borderRadius}px`,
		color: textColor,
		height: '44px',
		lineHeight: '44px',
		fontSize: '15px',
		fontWeight: 500,
		padding: '0 16px',
		border: 'none',
		cursor: 'pointer',
		boxSizing: 'border-box',
		display: 'block',
		textAlign: 'center',
		fontFamily: 'inherit',
	};

	if (variant === 'solid') {
		style.background = bgColor;
	} else if (variant === 'gradient') {
		const start = lightenHexColor(bgColor, 0.35);
		style.background = `linear-gradient(90deg, ${start} 0%, ${bgColor} 100%)`;
	} else if (variant === 'outline') {
		style.background = '#fff';
		style.border = `1px solid ${textColor}`;
		style.color = textColor;
	}

	return style;
}

export function getQrcodeNavCellStyle(blockType, data = {}, { h5 = false } = {}) {
	const normalized = normalizeQrcodeNavData(blockType, data);
	const borderRadius = normalized.borderRadius;
	const pxRatio = h5 ? 50 : 1;

	return {
		background: normalized.bgColor,
		color: normalized.textColor,
		borderRadius: h5 ? `${borderRadius / pxRatio}rem` : `${borderRadius}px`,
	};
}

export function getQrcodeNavCardStyle(blockType, data = {}, { h5 = false } = {}) {
	const normalized = normalizeQrcodeNavData(blockType, data);
	const borderRadius = normalized.borderRadius;
	const pxRatio = h5 ? 50 : 1;

	return {
		borderRadius: h5 ? `${borderRadius / pxRatio}rem` : `${borderRadius}px`,
	};
}

export function getQrcodeNavBannerStyle(blockType, data = {}, { h5 = false } = {}) {
	const normalized = normalizeQrcodeNavData(blockType, data);
	const borderRadius = normalized.borderRadius;
	const pxRatio = h5 ? 50 : 1;

	return {
		background: normalized.bgColor,
		color: normalized.textColor,
		borderRadius: h5 ? `${borderRadius / pxRatio}rem` : `${borderRadius}px`,
	};
}

export function replaceQrcodeNavCellImage(currentImage, file, createLocalImageItem) {
	if (currentImage?.local) {
		revokeLocalImageUrl(currentImage.previewUrl);
	}
	return createLocalImageItem(file);
}
